import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { getActEventBlockId, getEventBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { ErknmApproveRemarkChanges } from 'KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { ErknmApproveRemarkSectionCodeEnum } from 'KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';

import { ErknmActEventItemBody } from './ErknmActEventItemBody';
import { ErknmActEventItemHeaderPostfix } from './ErknmActEventItemHeaderPostfix';
import { checkHasErrorInCollapseBlock } from '../../../../../../functions/checkHasErrorInCollapseBlock';

interface IErknmActEventItemProps {
  watchOnly: boolean;
  name: string;
  actLevelName?: string;
  actId?: string;
  knmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  uniqKnmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  setUniqKnmEventTypesSelectOptions: Dispatch<SetStateAction<IDictionaryEventTypeSelectOption[] | undefined>>;
  actIndex?: number;
  isDecision?: boolean;
  isPm?: boolean;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isPaperless: boolean | undefined;
  value: IKnmErknmFormAct | IKnmPmFormAct | IDecisionActForm;
}

export const ErknmActEventItem = ({
  watchOnly,
  name,
  actId,
  knmEventTypesSelectOptions,
  uniqKnmEventTypesSelectOptions,
  setUniqKnmEventTypesSelectOptions,
  formRef,
  actIndex,
  isDecision,
  isPm,
  isPaperless,
  value,
  actLevelName,
}: IErknmActEventItemProps) => {
  const remove = formRef?.current.mutators.remove;

  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});

  const makeRemoveEventHandler = (index: number) => () => {
    // @ts-ignore
    const events: IKnmErknmFormEvent[] | undefined = formRef?.current.getFieldState(`${name}` as keyof IKnmErknmForm)
      ?.value;

    if (!watchOnly && remove && events) {
      remove(`${name}`, index);
    }
  };

  const validateEvents = useCallback(
    (events: IKnmErknmFormEvent[] | undefined) => {
      // @ts-ignore
      const isUnableToRefuse: boolean | undefined = formRef?.current.getFieldState('isUnableToRefuse')?.value;

      if (isPaperless && isPm && isUnableToRefuse && (!events || !events?.length)) {
        return 'Поле не может быть пустым';
      }
      if (isPaperless && !isPm && !isDecision && (!events || !events?.length)) {
        return 'Поле не может быть пустым';
      }
      if (isPaperless && isDecision && (!events || !events?.length)) {
        return 'Поле не может быть пустым';
      }
    },
    [isPaperless, isPm, isDecision],
  );

  return (
    <FieldArray name={name} validate={validateEvents}>
      {({ fields }: FieldArrayRenderProps<IKnmErknmFormEvent, HTMLElement>) => {
        if (!fields.length) {
          return null;
        }

        return fields.map((eventFieldName, sIndex) => {
          const fieldValue: IKnmErknmFormEvent = fields.value?.[sIndex];

          const id = actId ? getActEventBlockId(actId, fieldValue.listId) : getEventBlockId(fieldValue.listId);

          const getStopDateValue = () => (formRef?.current.getState().values as IKnmForm).stopDate;
          const stopDate = getStopDateValue();

          const isCanNotDelete = !!fieldValue?.protocol?.studiedDocuments?.find(document =>
            ['SD-1', 'SD-3'].includes(document?.status?.code || ''),
          );

          const hasError = checkHasErrorInCollapseBlock(formRef, eventFieldName);

          return (
            <KnmCollapse
              key={id}
              id={id}
              theme="fields"
              title={`Действие №${sIndex + 1}`}
              opened={fieldValue.isNew}
              onClose={
                (!watchOnly || fieldValue.isNew) && !isCanNotDelete && !fieldValue?.autoInnerEventForAddingFlag
                  ? makeRemoveEventHandler(sIndex)
                  : undefined
              }
              headerPostfix={<ErknmActEventItemHeaderPostfix isNew={fieldValue.isNew} />}
              additionalButtons={
                <ErknmApproveRemarkChanges
                  sectionCode={ErknmApproveRemarkSectionCodeEnum.EVENTS}
                  guid={fieldValue.guid}
                />
              }
              changeOpenedCallback={e =>
                setKnmCollapseOpened(state => ({
                  ...state,
                  [eventFieldName]: e,
                }))
              }
              error={hasError && knmCollapseOpened[eventFieldName]}
            >
              <ErknmActEventItemBody
                isWatchOnly={watchOnly}
                knmEventTypesSelectOptions={knmEventTypesSelectOptions}
                setUniqKnmEventTypesSelectOptions={setUniqKnmEventTypesSelectOptions}
                uniqKnmEventTypesSelectOptions={uniqKnmEventTypesSelectOptions}
                eventFieldName={eventFieldName}
                eventFieldValue={fieldValue}
                eventId={id}
                stopDate={stopDate}
                actIndex={actIndex}
                eventIndex={sIndex}
                formRef={formRef}
                isDecision={isDecision}
                isPm={isPm}
                innerEvents={fields.value}
                value={value}
                actLevelName={actLevelName}
              />
            </KnmCollapse>
          );
        });
      }}
    </FieldArray>
  );
};
